import { Device, type types as msTypes } from 'mediasoup-client';
import type { SignalingMessage } from '../types';

export interface ProducerHandle {
  stop: () => void;
  publishWebcam: () => Promise<void>;
  /** Currently-active local stream (if any) — useful for self-preview. */
  localStream: () => MediaStream | null;
}

interface UseMediasoupProducerOptions {
  wsUrl: string;
  token: string;
  onError?: (err: Error) => void;
}

/**
 * Student-side: connects as a producer-role peer, ready to publish webcam
 * (and later screen) tracks. Call `publishWebcam()` once the user grants
 * getUserMedia permission.
 */
export async function startMediasoupProducer(opts: UseMediasoupProducerOptions): Promise<ProducerHandle> {
  const ws = new WebSocket(`${opts.wsUrl}/ws?token=${encodeURIComponent(opts.token)}`);
  const pending = new Map<string, (msg: SignalingMessage) => void>();
  let nextRequestId = 0;
  let closed = false;
  let device: Device | null = null;
  let sendTransport: msTypes.Transport | null = null;
  let stream: MediaStream | null = null;
  const producers = new Map<string, msTypes.Producer>();

  const send = (msg: SignalingMessage) => ws.send(JSON.stringify(msg));

  const request = <T>(type: string, data?: unknown) =>
    new Promise<T>((resolve, reject) => {
      const id = `req-${++nextRequestId}`;
      pending.set(id, (reply) => {
        if (reply.error) reject(new Error(reply.error.message));
        else resolve(reply.data as T);
      });
      send({ id, type, data });
    });

  const setup = async() => {
    const rtpCapabilities = await request<msTypes.RtpCapabilities>('getRouterRtpCapabilities');
    device = new Device();
    await device.load({ routerRtpCapabilities: rtpCapabilities });

    const transportInfo = await request<{
      id: string;
      iceParameters: msTypes.IceParameters;
      iceCandidates: msTypes.IceCandidate[];
      dtlsParameters: msTypes.DtlsParameters;
      iceServers?: RTCIceServer[];
    }>('createWebRtcTransport');

    sendTransport = device.createSendTransport({
      ...transportInfo,
      iceServers: transportInfo.iceServers ?? [],
    });

    sendTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
      request('connectTransport', { transportId: transportInfo.id, dtlsParameters })
        .then(() => callback())
        .catch((e: Error) => errback(e));
    });

    sendTransport.on('produce', ({ kind, rtpParameters, appData }, callback, errback) => {
      request<{ producerId: string }>('produce', {
        transportId: transportInfo.id,
        kind,
        rtpParameters,
        appData,
      })
        .then(({ producerId }) => callback({ id: producerId }))
        .catch((e: Error) => errback(e));
    });
  };

  /**
   * Defaults tuned for high-fanout monitoring (1000+ students per session).
   * At 320x240/10fps with VP8 the upstream is ~80–150 kbps per student;
   * admins decoding multiple streams in a grid stay responsive. Callers
   * can pass `quality` to override.
   */
  const QUALITY_PRESETS = {
    low: { width: 240, height: 180, frameRate: 8 },     // ~50 kbps, 1000+ student exams
    standard: { width: 320, height: 240, frameRate: 10 }, // ~100 kbps (default)
    high: { width: 640, height: 480, frameRate: 15 },    // ~250 kbps, small classroom
  } as const;

  type QualityPreset = keyof typeof QUALITY_PRESETS;

  const publishWebcam = async(opts: { quality?: QualityPreset } = {}) => {
    if (!sendTransport) throw new Error('Producer not ready');
    if (stream) return;

    const preset = QUALITY_PRESETS[opts.quality ?? 'standard'];
    stream = await navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: preset.width },
        height: { ideal: preset.height },
        frameRate: { ideal: preset.frameRate },
      },
      audio: false,
    });
    for (const track of stream.getVideoTracks()) {
      const producer = await sendTransport.produce({ track, appData: { source: 'webcam' } });
      producers.set(producer.id, producer);
      producer.on('trackended', () => producer.close());
    }
  };

  const stop = () => {
    closed = true;
    for (const p of producers.values()) p.close();
    producers.clear();
    sendTransport?.close();
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      stream = null;
    }
    ws.close();
  };

  return new Promise((resolve, reject) => {
    ws.onopen = async() => {
      try {
        await setup();
        resolve({ stop, publishWebcam, localStream: () => stream });
      } catch (err) {
        reject(err);
      }
    };

    ws.onerror = () => {
      if (!closed) opts.onError?.(new Error('Signaling websocket error'));
    };

    ws.onclose = () => {
      closed = true;
    };

    ws.onmessage = (raw) => {
      let parsed: SignalingMessage;
      try {
        parsed = JSON.parse(typeof raw.data === 'string' ? raw.data : '');
      } catch {
        return;
      }
      if (!parsed.id) return;
      const handler = pending.get(parsed.id);
      if (handler) {
        pending.delete(parsed.id);
        handler(parsed);
      }
    };
  });
}
